import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { pb, TutorProfile } from "@/lib/pocketbase";
import { useSavedTutors } from "@/hooks/useSavedTutors";
import { useReviews } from "@/hooks/useReviews";
import { useLessons } from "@/hooks/useLessons";
import { useToast } from "@/hooks/use-toast";
import { useCurrencyFormat } from "@/hooks/useCurrencyFormat";
import { useCurrency, currencies } from "@/contexts/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Star,
  Heart,
  Clock,
  Globe,
  GraduationCap,
  Video,
  MessageCircle,
  Calendar,
  MapPin,
  BookOpen,
  Award,
  CheckCircle,
  Users,
  Play,
  Zap,
  AlertTriangle,
  CreditCard,
  Smartphone,
  ChevronDown,
  DollarSign,
  MessageSquare,
  X,
  ArrowRight,
} from "lucide-react";
import { PriceWithUsdBadge } from "@/components/tutor/PriceWithUsdBadge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import SendMessageModal from "@/components/SendMessageModal";
import TutorReviews from "@/components/TutorReviews";
import ImageModal from "@/components/ImageModal";
import { getProfilePictureUrl, getUserInitials } from "@/utils/profileUtils";
import { convertToEmbeddableUrl } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import TutorCountryFlag from "@/components/TutorCountryFlag";
import LessonCard from "@/components/LessonCard";
import TutorDetailsTabs from "@/components/TutorDetailsTabs";
import { TutorPostsTab } from "@/components/TutorPostsTab";
import { useRef } from "react";
import { LearningPlanModal } from "@/components/tutor/LearningPlanModal";
import { detectTextDirection, wrapTextWithBidiMarkers } from "@/utils/textDirection";
import { capitalizeSubject } from "@/utils/tutorDataUtils";
import { trackAddToCart } from "@/lib/metaPixel";
import TutorAvailabilityCalendar from "@/components/TutorAvailabilityCalendar";
import { useActiveDiscount } from "@/hooks/useActiveDiscount";

const TutorDetails = () => {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { toggleSavedTutor, isTutorSaved } = useSavedTutors();
  const [searchParams] = useSearchParams();
  const [tutor, setTutor] = useState<TutorProfile | null>(null);
  const [localCoachInfo, setLocalCoachInfo] = useState<any>(null);
  const [localMusicCoachInfo, setLocalMusicCoachInfo] = useState<any>(null);
  const [localTravelGuideInfo, setLocalTravelGuideInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isLearningPlanOpen, setIsLearningPlanOpen] = useState(false);
  const [bookingType, setBookingType] = useState<"regular" | "coaching" | "music_coaching" | "travel_coaching">(
    "regular",
  );
  const [showFullBio, setShowFullBio] = useState(false);
  const [similarTutors, setSimilarTutors] = useState<TutorProfile[]>([]);
  const [isTogglingDisplay, setIsTogglingDisplay] = useState(false);
  const [showVideoBanner, setShowVideoBanner] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [introVideoUrl, setIntroVideoUrl] = useState("");
  const [isYoutubeVideo, setIsYoutubeVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reset video banner and URL when tutor changes
  useEffect(() => {
    setShowVideoBanner(false);
    setIntroVideoUrl("");
    setIsYoutubeVideo(false);
  }, [tutorId]);

  const { formatPrice, convertPrice, selectedCurrency, currencies: contextCurrencies } = useCurrencyFormat();
  const { activeDiscount, appliesToType } = useActiveDiscount();
  // Get dynamic data for reviews and lessons
  const { reviews, calculateAverageRating } = useReviews(tutorId);

  // Get lessons data for the tutor (using their user ID once we have tutor data)
  const { lessons } = useLessons(tutor?.user);

  // Check if user can add review (student who has booked this tutor)
  const canAddReview = user && user.role === "student";

  // Calculate dynamic stats with default rating
  const averageRating = calculateAverageRating();
  const reviewCount = reviews.length;
  const lessonsCount = lessons.length;

  // Use 5.0 as default rating when no reviews exist
  const displayRating = reviewCount > 0 ? averageRating : 5.0;

  // Check if user is admin or assistant (both can view hidden tutors)
  const isAdmin = user && user.role === "admin";
  const isAssistant = user && user.role === "assistant";
  const canViewHiddenTutors = isAdmin || isAssistant;
  
  // Debug: Log role check on every render
  console.log("🔍 [TutorDetails] Role check:", { 
    userId: user?.id, 
    userRole: user?.role, 
    isAdmin, 
    isAssistant, 
    canViewHiddenTutors 
  });

  // Helper to open booking modal and track AddToCart
  const openBookingWithTracking = (type: "regular" | "coaching" | "music_coaching" | "travel_coaching" = "regular") => {
    if (tutor) {
      trackAddToCart({
        id: tutor.id,
        title: tutor.name || "Tutor Session",
        price: tutor.hourlyRate || 0,
        category: tutor.subjects?.join(", ") || "Tutoring",
      }, 1, {
        tutor_name: tutor.name || "Unknown Tutor",
        student_name: user?.name || "Unknown Student",
        student_email: user?.email || "",
        subject: tutor.subjects?.[0] || "Tutoring"
      });
    }
    setBookingType(type);
    setIsBookingModalOpen(true);
  };

  const fetchSimilarTutors = async (subjects: string[], excludeTutorId?: string) => {
    try {
      if (!subjects || subjects.length === 0) return;

      // Create filter for any of the subjects
      const subjectFilters = subjects.map((sub) => `subjects ~ "${sub}"`).join(" || ");
      const filters = [
        `(${subjectFilters})`,
        `is_displayed = true`,
        `is_suspended != true`,
        `isFrozen != true`,
        `profilePicture != ""`,
      ];
      if (excludeTutorId) {
        filters.push(`id != "${excludeTutorId}"`);
      }
      // Fetch top 2 tutors sorted by highest evaluation (1-10 scale)
      const tutorsResult = await pb.collection("tutors").getList(1, 2, {
        filter: filters.join(" && "),
        sort: "-evaluation,-created",
      });
      setSimilarTutors(tutorsResult.items as TutorProfile[]);
    } catch (error) {
      console.error("Error fetching similar tutors:", error);
    }
  };

  const toggleDisplayStatus = async () => {
    if (!tutor || !isAdmin) return;

    setIsTogglingDisplay(true);
    try {
      // Toggle: if currently hidden (false) -> show (true), if shown (true or undefined) -> hide (false)
      const currentlyDisplayed = tutor.is_displayed !== false; // treat undefined as true
      const newStatus = !currentlyDisplayed; // flip it

      await pb.collection("tutors").update(tutor.id, {
        is_displayed: newStatus,
      });

      setTutor({ ...tutor, is_displayed: newStatus });
      toast({
        title: "Success",
        description: `Tutor ${newStatus ? "displayed" : "hidden"} successfully`,
      });
    } catch (error) {
      console.error("Error toggling display status:", error);
      toast({
        title: "Error",
        description: "Failed to update tutor display status",
        variant: "destructive",
      });
    } finally {
      setIsTogglingDisplay(false);
    }
  };

  useEffect(() => {
    const fetchTutor = async () => {
      if (!tutorId) return;

      // Compute staff access directly from user state to ensure we have latest value
      const hasStaffAccess = user && (user.role === "admin" || user.role === "assistant");
      console.log("🔍 [TutorDetails useEffect] Staff access check:", { 
        userId: user?.id, 
        userRole: user?.role, 
        hasStaffAccess 
      });

      try {
        // First try to get the tutor directly by ID (assuming it's a tutor profile ID)
        let tutorRecord;
        try {
          tutorRecord = await pb.collection("tutors").getOne(tutorId, {
            expand: "user",
          });
        } catch (directError) {
          // If direct fetch fails, try to find tutor by user ID
          console.log("Direct tutor fetch failed, trying by user ID...");
          const tutorRecords = await pb.collection("tutors").getList(1, 1, {
            filter: `user = "${tutorId}"`,
            expand: "user",
          });

          if (tutorRecords.items.length > 0) {
            tutorRecord = tutorRecords.items[0];
          } else {
            throw new Error("Tutor not found");
          }
        }

        setTutor(tutorRecord as TutorProfile);

        // Set intro video URL after tutor is loaded
        if (tutorRecord.introVideoFile && tutorRecord.introVideoFile.trim()) {
          setIntroVideoUrl(pb.files.getURL(tutorRecord, tutorRecord.introVideoFile));
          setIsYoutubeVideo(false);
          setShowVideoBanner(true);
        } else if (tutorRecord.introVideo && tutorRecord.introVideo.trim()) {
          // Use convertToEmbeddableUrl for YouTube/external URLs
          setIntroVideoUrl(convertToEmbeddableUrl(tutorRecord.introVideo));
          setIsYoutubeVideo(true);
          setShowVideoBanner(true);
        }

        // Check if tutor is unavailable: hidden, suspended, or no profile picture
        // Allow staff (admin/assistant) to view anyway
        const hasNoPhoto = !tutorRecord.profilePicture || tutorRecord.profilePicture.trim() === "";
        const isUnavailable = tutorRecord.is_displayed === false || tutorRecord.is_suspended === true || hasNoPhoto;

        console.log("🔍 [TutorDetails useEffect] Tutor availability:", { 
          tutorId: tutorRecord.id,
          is_displayed: tutorRecord.is_displayed,
          is_suspended: tutorRecord.is_suspended,
          hasNoPhoto,
          isUnavailable,
          hasStaffAccess,
          willShowBlockedPage: isUnavailable && !hasStaffAccess
        });

        if (isUnavailable && !hasStaffAccess) {
          // Fetch similar tutors based on primary subject only (first subject)
          if (tutorRecord.subjects && tutorRecord.subjects.length > 0) {
            await fetchSimilarTutors([tutorRecord.subjects[0]], tutorRecord.id);
          }
          setIsLoading(false);
          return; // Don't load additional coach info if tutor is blocked
        }

        // Check if this tutor is also a local fitness coach
        try {
          const coachRecords = await pb.collection("local_fitness_coaches").getList(1, 1, {
            filter: `tutorId = "${tutorRecord.id}" && isActive = true && isVerified = true`,
          });

          if (coachRecords.items.length > 0) {
            setLocalCoachInfo(coachRecords.items[0]);
          }
        } catch (coachError) {
          console.error("Error checking local fitness coach info:", coachError);
        }

        // Check if this tutor is also a local music coach
        try {
          const musicCoachRecords = await pb.collection("local_music_coaches").getList(1, 1, {
            filter: `tutorId = "${tutorRecord.id}" && isActive = true && isVerified = true`,
          });

          if (musicCoachRecords.items.length > 0) {
            const rawMusicCoachData = musicCoachRecords.items[0];

            // Process the data to handle underscore prefixes
            const processedMusicCoachData = {
              ...rawMusicCoachData,
              hourlyRate: rawMusicCoachData.hourlyRate || rawMusicCoachData._hourlyRate || 0,
              location: rawMusicCoachData.location || rawMusicCoachData._location || { city: "", state: "" },
              specialties: rawMusicCoachData.specialties || rawMusicCoachData._specialties || [],
              coverageRadius: rawMusicCoachData.coverageRadius || rawMusicCoachData._coverageRadius || 10,
              acceptsHomeVisits: rawMusicCoachData.acceptsHomeVisits || rawMusicCoachData._acceptsHomeVisits || false,
              hasOwnInstruments: rawMusicCoachData.hasOwnInstruments || rawMusicCoachData._hasOwnInstruments || false,
              experience: rawMusicCoachData.experience || rawMusicCoachData._experience || 0,
            };

            setLocalMusicCoachInfo(processedMusicCoachData);
          }
        } catch (musicCoachError) {
          console.error("Error checking local music coach info:", musicCoachError);
        }

        // Check if this tutor is also a local travel guide
        try {
          const travelGuideRecords = await pb.collection("local_travel_guides").getList(1, 1, {
            filter: `tutorId = "${tutorRecord.id}" && isActive = true && isVerified = true`,
          });

          if (travelGuideRecords.items.length > 0) {
            const rawTravelGuideData = travelGuideRecords.items[0];

            // Process the data to handle underscore prefixes
            const processedTravelGuideData = {
              ...rawTravelGuideData,
              hourlyRate: rawTravelGuideData.hourlyRate || rawTravelGuideData._hourlyRate || 0,
              location: rawTravelGuideData.location || rawTravelGuideData._location || { city: "", state: "" },
              specialties: rawTravelGuideData.specialties || rawTravelGuideData._specialties || [],
              coverageRadius: rawTravelGuideData.coverageRadius || rawTravelGuideData._coverageRadius || 10,
              acceptsHomeVisits: rawTravelGuideData.acceptsHomeVisits || rawTravelGuideData._acceptsHomeVisits || false,
              hasOwnTransport: rawTravelGuideData.hasOwnTransport || rawTravelGuideData._hasOwnTransport || false,
              experience: rawTravelGuideData.experience || rawTravelGuideData._experience || 0,
            };

            setLocalTravelGuideInfo(processedTravelGuideData);
          }
        } catch (travelGuideError) {
          console.error("Error checking local travel guide info:", travelGuideError);
        }
      } catch (error) {
        console.error("Error fetching tutor:", error);
        toast({
          title: "Error",
          description: "Failed to load tutor profile",
          variant: "destructive",
        });
        navigate("/tutors");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTutor();
  }, [tutorId, navigate, toast, user]);

  // Handle redirect after authentication - store current URL before redirecting to login
  useEffect(() => {
    if (user) {
      const redirectUrl = localStorage.getItem("redirectAfterAuth");
      if (redirectUrl && redirectUrl === window.location.pathname + window.location.search) {
        localStorage.removeItem("redirectAfterAuth");
        // Optional: Show a welcome message
        toast({
          title: "Welcome back!",
          description: "You can now write a review for this tutor.",
          duration: 3000,
        });
      }
    }
  }, [user, toast]);

  // Handle openMessage URL parameter
  useEffect(() => {
    const openMessage = searchParams.get("openMessage");
    if (openMessage === "true" && tutor) {
      setIsMessageModalOpen(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [tutor, searchParams]);

  // Auto-play video in banner (only for uploaded videos, not YouTube)
  useEffect(() => {
    const playVideo = async () => {
      if (videoRef.current && showVideoBanner && !isYoutubeVideo) {
        try {
          console.log("🎬 Attempting to autoplay video");
          await videoRef.current.play();
          console.log("✅ Video autoplay successful");
        } catch (error) {
          console.error("❌ Autoplay prevented:", error);
        }
      }
    };

    // Small delay to ensure video is loaded
    const timer = setTimeout(playVideo, 100);
    return () => clearTimeout(timer);
  }, [showVideoBanner, isYoutubeVideo]);

  const getTutorName = (tutor: TutorProfile) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return "Unknown Tutor";
  };

  const formatAvailability = (availability: any) => {
    if (!availability?.timeSlots || availability.timeSlots.length === 0) {
      return "No schedule set";
    }

    return availability.timeSlots
      .filter((daySlot: any) => daySlot.slots && daySlot.slots.length > 0)
      .map((daySlot: any) => {
        const timeRanges = daySlot.slots.map((slot: any) => `${slot.start}-${slot.end}`).join(", ");
        return `${daySlot.day}: ${timeRanges}`;
      })
      .join(" | ");
  };

  const getResponseTime = (tutor: TutorProfile) => {
    if (tutor.responseTimeHours) {
      if (tutor.responseTimeHours < 1) {
        return "Within 1 hour";
      } else if (tutor.responseTimeHours === 1) {
        return "Within 1 hour";
      } else {
        return `Within ${tutor.responseTimeHours} hours`;
      }
    }
    return "Within 24 hours";
  };

  const getTotalLessons = (tutor: TutorProfile) => {
    return tutor.totalLessons || 0;
  };

  const getOnlineStatus = (tutor: TutorProfile) => {
    if (tutor.isOnline) {
      return { status: "Online", color: "text-green-600", dotColor: "bg-green-500" };
    }

    if (tutor.lastSeen) {
      const lastSeenDate = new Date(tutor.lastSeen);
      const now = new Date();
      const diffHours = Math.floor((now.getTime() - lastSeenDate.getTime()) / (1000 * 60 * 60));

      if (diffHours < 1) {
        return { status: "Recently active", color: "text-yellow-600", dotColor: "bg-yellow-500" };
      } else if (diffHours < 24) {
        return { status: `Active ${diffHours}h ago`, color: "text-gray-600", dotColor: "bg-gray-500" };
      }
    }

    return { status: "Offline", color: "text-gray-600", dotColor: "bg-gray-500" };
  };

  const handleToggleSave = () => {
    if (!tutor) return;
    toggleSavedTutor(tutor);
    toast({
      title: isTutorSaved(tutor.id) ? "Tutor Removed" : "Tutor Saved!",
      description: isTutorSaved(tutor.id) ? "Tutor removed from your saved list" : "Tutor added to your saved list",
      duration: 3000,
    });
  };

  // Use the shared utility function for video URL conversion
  const getYouTubeEmbedUrl = convertToEmbeddableUrl;

  // Helper function to categorize subjects
  const categorizeSubjects = (subjects: string[]) => {
    const defaultSubjects = [
      "Mathematics",
      "English",
      "Spanish",
      "Physics",
      "Chemistry",
      "Biology",
      "Literature",
      "English Language",
      "French",
      "German",
      "Italian",
      "Portuguese",
      "Chinese",
      "Japanese",
      "Korean",
      "Arabic",
      "Programming",
      "Business",
      "Marketing",
      "Finance",
      "Data Science",
      "Web Development",
      "Graphic Design",
      "Fitness & Bodybuilding",
      "Cooking",
      "Music",
      "Art",
      "Photography",
      "Travel",
      "Conversation",
    ];

    const defaultOnes = subjects.filter((subject) => defaultSubjects.includes(subject));
    const approvedOnes = subjects.filter((subject) => !defaultSubjects.includes(subject));

    return { defaultOnes, approvedOnes };
  };

  // Helper function to format price - converts from tutor's currency to selected display currency
  const formatTutorPrice = (price: number) => {
    if (!price || price <= 0) return "";

    const tutorCurrency = tutor?.pricingCurrency || "USD";
    const tutorCurrencyData = contextCurrencies.find((c) => c.code === tutorCurrency);

    // Convert tutor's price to USD first
    let priceInUSD = price;
    if (tutorCurrency !== "USD" && tutorCurrencyData) {
      priceInUSD = price / tutorCurrencyData.rate;
    }

    // Use the context's formatPrice to ensure consistent conversion and rounding
    const formatted = formatPrice(priceInUSD);

    // Return empty string if formatting resulted in empty (price too small)
    if (!formatted) return "";

    // For EGP, convert to Arabic numerals
    if (selectedCurrency.code === "EGP") {
      return formatted.replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[parseInt(d)]);
    }

    return formatted;
  };

  // Helper function to sort levels in correct order: beginner → intermediate → advanced
  const sortLevelRates = (levelRates: Array<[string, unknown]>) => {
    const levelOrder: { [key: string]: number } = {
      beginner: 1,
      intermediate: 2,
      advanced: 3,
    };

    return levelRates.sort((a, b) => {
      const orderA = levelOrder[a[0].toLowerCase()] || 999;
      const orderB = levelOrder[b[0].toLowerCase()] || 999;
      return orderA - orderB;
    });
  };

  // Helper function to get localized text for time units
  const getLocalizedText = (text: string) => {
    if (selectedCurrency.code === "EGP") {
      switch (text) {
        case "per hour":
          return "في الساعة";
        case "hour":
          return "ساعة";
        case "level":
          return "مستوى";
        case "per session":
          return "في الجلسة";
        case "per month":
          return "في الشهر";
        case "Per Hour":
          return "في الساعة";
        case "Per Session":
          return "في الجلسة";
        case "By Level":
          return "حسب المستوى";
        case "Monthly Subscription":
          return "اشتراك شهري";
        case "Pricing by Level":
          return "التسعير حسب المستوى";
        case "pricing TBD":
          return "سعر غير محدد";
        default:
          return text;
      }
    }
    return text;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white overflow-x-hidden w-full flex-1">
        <Navbar />
        <div className="flex items-center justify-center w-full" style={{ minHeight: "calc(100vh - 4rem)" }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!tutor) {
    return (
      <div className="min-h-screen bg-white overflow-x-hidden flex-1">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Tutor Not Found</h1>
          <Button onClick={() => navigate("/tutors")}>Back to Tutors</Button>
        </div>
      </div>
    );
  }

  // Show blocked tutor page if unavailable (hidden, suspended, or no photo) and user is not staff
  const hasNoPhoto = !tutor.profilePicture || tutor.profilePicture.trim() === "";
  const isUnavailable = tutor.is_displayed === false || tutor.is_suspended === true || hasNoPhoto;

  console.log("🔍 [TutorDetails Render] Blocked page check:", { 
    tutorId: tutor.id,
    is_displayed: tutor.is_displayed,
    isUnavailable,
    userRole: user?.role,
    canViewHiddenTutors,
    willShowBlockedPage: isUnavailable && !canViewHiddenTutors
  });

  if (isUnavailable && !canViewHiddenTutors) {
    return (
      <div className="min-h-screen bg-gray-50 overflow-x-hidden flex-1">
        <Navbar />
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <Card className="p-8 text-center">
            <div className="mb-6">
              <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Tutor Unavailable</h1>
              <p className="text-lg text-gray-600">This tutor is currently not available for bookings.</p>
            </div>

            {similarTutors.length > 0 && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Check out similar {tutor.subjects?.[0] || "tutors"} tutors
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {similarTutors.map((similarTutor) => (
                    <Card
                      key={similarTutor.id}
                      className="cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => navigate(`/tutor/${similarTutor.id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          <Avatar className="h-20 w-20 mb-3">
                            <AvatarImage src={getProfilePictureUrl(similarTutor)} />
                            <AvatarFallback>{getUserInitials(getTutorName(similarTutor))}</AvatarFallback>
                          </Avatar>
                          <h3 className="font-semibold text-lg mb-1">{getTutorName(similarTutor)}</h3>
                          <p className="text-sm text-gray-600 mb-2">{similarTutor.shortBio || "Professional Tutor"}</p>
                          <Badge variant="secondary">{similarTutor.subject}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <Button size="lg" onClick={() => navigate("/tutors")}>
                Browse All Tutors
              </Button>
            </div>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const tutorName = getTutorName(tutor);
  const profilePictureUrl = getProfilePictureUrl(tutor);
  const responseTime = getResponseTime(tutor);
  const totalLessons = getTotalLessons(tutor);
  const onlineStatus = getOnlineStatus(tutor);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden flex-1">
      <Navbar />

      {/* Video Banner */}
      {!isLoading && introVideoUrl && showVideoBanner && (
        <div
          className="fixed top-20 right-2 md:right-4 z-50 w-32 md:w-36 bg-background/40 backdrop-blur-sm border rounded-lg shadow-lg overflow-hidden cursor-pointer transition-all hover:bg-background/50"
          onClick={() => setIsVideoModalOpen(true)}
        >
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-0.5 right-0.5 z-10 bg-red-500 hover:bg-red-600 text-white h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                setShowVideoBanner(false);
              }}
            >
              <X className="h-3 w-3" />
            </Button>
            <div className="p-1.5 md:p-2">
              <div className="flex items-center gap-1 mb-1">
                <Video className="h-2.5 w-2.5 md:h-3 md:w-3 text-primary" />
                <span className="font-semibold text-[10px] md:text-xs">Intro</span>
              </div>
              <div className="relative">
                {isYoutubeVideo ? (
                  <div className="relative">
                    <iframe
                      src={`${introVideoUrl}?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&modestbranding=1&rel=0`}
                      className="w-full rounded-md max-h-32 md:max-h-40 object-cover opacity-90 pointer-events-none"
                      allow="autoplay; encrypted-media"
                    />
                    {/* Overlay to prevent YouTube redirects */}
                    <div className="absolute inset-0 cursor-pointer" />
                  </div>
                ) : (
                  <video
                    ref={videoRef}
                    src={introVideoUrl}
                    className="w-full rounded-md max-h-32 md:max-h-40 object-cover opacity-90"
                    muted
                    loop
                    playsInline
                    autoPlay
                    onLoadedData={() => {
                      console.log("🎬 Video loaded, attempting play");
                      videoRef.current?.play();
                    }}
                  />
                )}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  {activeDiscount && appliesToType('bookings') && (
                    <span className="bg-red-500 text-white text-[8px] md:text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse shadow-lg mb-1" dir="rtl">
                      🔥 خصم {activeDiscount.percentage}%
                    </span>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center justify-end pb-1 pointer-events-none bg-gradient-to-t from-black/40 to-transparent rounded-b-md">
                  <Play className="h-4 w-4 md:h-5 md:w-5 text-white drop-shadow-lg mb-0.5" />
                  <span className="text-white text-[9px] md:text-[10px] font-medium drop-shadow-md text-center px-1 pb-1">
                    Click to see the full intro
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl bg-background rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-10 bg-background/80 hover:bg-background"
              onClick={() => setIsVideoModalOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="p-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Video className="h-5 w-5 text-primary" />
                Introduction Video
              </h3>
              {isYoutubeVideo ? (
                <div className="relative w-full aspect-video">
                  <iframe
                    src={`${introVideoUrl}?autoplay=1&controls=1&modestbranding=1&rel=0&disablekb=0&showinfo=0`}
                    className="w-full h-full rounded-md pointer-events-auto"
                    allow="autoplay; encrypted-media; fullscreen"
                  />
                  {/* Top overlay to block channel name clicks */}
                  <div
                    className="absolute top-0 left-0 right-0 h-14 cursor-default"
                    onClick={(e) => e.preventDefault()}
                  />
                  {/* Bottom-right overlay to block YouTube logo */}
                  <div
                    className="absolute bottom-0 right-0 w-24 h-10 cursor-default"
                    onClick={(e) => e.preventDefault()}
                  />
                </div>
              ) : (
                <video src={introVideoUrl} controls autoPlay className="w-full rounded-md" preload="auto" />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 pt-20 pb-8 max-w-full overflow-x-hidden">
        {/* Admin Controls */}
        {isAdmin && (
          <div className="mb-6">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Admin Controls</h3>
                  <p className="text-sm text-gray-600">
                    Tutor is currently {tutor.is_displayed === false ? "hidden from" : "visible to"} users
                  </p>
                </div>
                <Button
                  onClick={toggleDisplayStatus}
                  disabled={isTogglingDisplay}
                  variant={tutor.is_displayed === false ? "default" : "destructive"}
                  size="lg"
                >
                  {isTogglingDisplay ? "Updating..." : tutor.is_displayed === false ? "Show Tutor" : "Hide Tutor"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 min-w-0">
          {/* Left Column - Tutor Info */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Main Profile Card */}
            <Card className="overflow-hidden">
              <CardContent className="p-0 relative">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 sm:p-6 text-white overflow-x-hidden">
                  {/* Mobile Layout - Stacked */}
                  <div className="sm:hidden space-y-3">
                    {/* Photo and basic info */}
                    <div className="flex items-start gap-3">
                      <div className="relative shrink-0">
                        <Avatar
                          className="h-16 w-16 border-2 border-white shadow-lg cursor-pointer"
                          onClick={() => profilePictureUrl && setIsPhotoModalOpen(true)}
                        >
                          <AvatarImage src={profilePictureUrl} />
                          <AvatarFallback className="text-lg bg-white text-blue-600">
                            {getUserInitials(tutorName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                        {tutor.country && (
                          <div className="absolute -top-1 -left-1">
                            <TutorCountryFlag
                              countryCode={tutor.country}
                              size="lg"
                              className="bg-white rounded-full p-0.5 shadow-sm scale-75"
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h1 className="text-xl font-bold mb-1 break-words">{tutorName}</h1>
                        <div
                          className="cursor-pointer"
                          onClick={() => tutor.shortBio && tutor.shortBio.length > 60 && setShowFullBio(!showFullBio)}
                        >
                          <p className={`text-sm text-blue-100 break-words ${showFullBio ? "" : "line-clamp-2"}`}>
                            {tutor.shortBio || "Professional Tutor"}
                          </p>
                          {tutor.shortBio && tutor.shortBio.length > 60 && (
                            <span className="text-xs text-white underline mt-1 inline-block">
                              {showFullBio ? "See less" : "See more"}
                            </span>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20 h-8 w-8 p-0 shrink-0"
                        onClick={handleToggleSave}
                      >
                        <Heart className={`h-4 w-4 ${isTutorSaved(tutor.id) ? "fill-current" : ""}`} />
                      </Button>
                    </div>

                    {/* Rating and stats */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center">
                        <div className="flex mr-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${i < Math.round(displayRating) ? "text-yellow-400 fill-current" : "text-white/50"}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-semibold">{displayRating}</span>
                        <span className="text-xs text-blue-100 ml-1">({reviewCount})</span>
                      </div>
                    </div>

                    {/* Country */}
                    {tutor.country && (
                      <div className="flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        <TutorCountryFlag countryCode={tutor.country} showName={true} size="sm" />
                      </div>
                    )}

                    {/* Session Preference */}
                    {tutor.sessionPreference && (
                      <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs w-fit">
                        {tutor.sessionPreference === "chat_only" && "💬 Chat & Messages Only"}
                        {tutor.sessionPreference === "video_only" && "📹 Video Sessions Only"}
                        {tutor.sessionPreference === "both" && "🎯 Chat & Video Sessions"}
                      </Badge>
                    )}

                    {/* Subjects */}
                    {tutor.subjects && tutor.subjects.length > 0 && (
                      <div className="space-y-1">
                        {(() => {
                          const { defaultOnes, approvedOnes } = categorizeSubjects(tutor.subjects);
                          return (
                            <>
                              {defaultOnes.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {defaultOnes.slice(0, 3).map((subject, index) => (
                                    <Badge
                                      key={index}
                                      variant="secondary"
                                      className="bg-white/20 text-white border-0 text-[10px]"
                                    >
                                      {capitalizeSubject(subject)}
                                    </Badge>
                                  ))}
                                  {defaultOnes.length > 3 && (
                                    <Badge variant="secondary" className="bg-white/20 text-white border-0 text-[10px]">
                                      +{defaultOnes.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              )}
                              {approvedOnes.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {approvedOnes.slice(0, 2).map((subject, index) => (
                                    <Badge
                                      key={index}
                                      variant="secondary"
                                      className="bg-yellow-400/20 text-yellow-100 border-yellow-300/30 text-[10px]"
                                    >
                                      {capitalizeSubject(subject)}
                                    </Badge>
                                  ))}
                                  {approvedOnes.length > 2 && (
                                    <Badge
                                      variant="secondary"
                                      className="bg-yellow-400/20 text-yellow-100 border-yellow-300/30 text-[10px]"
                                    >
                                      +{approvedOnes.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => openBookingWithTracking("regular")}
                        className="flex-1 bg-white text-blue-600 hover:bg-blue-50 h-9 text-sm font-medium shadow-md"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Book Session - ابدأ الحجز
                      </Button>

                      <Button
                        onClick={() => setIsMessageModalOpen(true)}
                        className="flex-1 bg-white text-blue-600 hover:bg-blue-50 h-9 text-sm font-medium shadow-md"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Message
                      </Button>
                    </div>
                  </div>

                  {/* Desktop Layout - Horizontal */}
                  <div className="hidden sm:flex items-start justify-between gap-6 min-w-0">
                    {/* Left: Photo and basic info */}
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                      <div className="relative shrink-0">
                        <Avatar
                          className="h-24 w-24 border-4 border-white shadow-lg cursor-pointer hover:scale-105 transition-transform duration-200"
                          onClick={() => profilePictureUrl && setIsPhotoModalOpen(true)}
                        >
                          <AvatarImage src={profilePictureUrl} />
                          <AvatarFallback className="text-2xl bg-white text-blue-600">
                            {getUserInitials(tutorName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2">
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        </div>
                        {tutor.country && (
                          <div className="absolute -top-2 -left-2">
                            <TutorCountryFlag
                              countryCode={tutor.country}
                              size="lg"
                              className="bg-white rounded-full p-1 shadow-sm"
                            />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h1 className="text-3xl font-bold mb-2 break-words">{tutorName}</h1>
                        <div
                          className="cursor-pointer mb-3"
                          onClick={() => tutor.shortBio && tutor.shortBio.length > 80 && setShowFullBio(!showFullBio)}
                        >
                          <p className={`text-base text-blue-100 break-words ${showFullBio ? "" : "line-clamp-2"}`}>
                            {tutor.shortBio || "Professional Tutor"}
                          </p>
                          {tutor.shortBio && tutor.shortBio.length > 80 && (
                            <button className="text-sm text-white underline mt-1 hover:text-blue-100 transition-colors">
                              {showFullBio ? "See less" : "See more"}
                            </button>
                          )}
                        </div>

                        {tutor.country && (
                          <div className="flex items-center mb-3">
                            <MapPin className="h-4 w-4 mr-2" />
                            <TutorCountryFlag countryCode={tutor.country} showName={true} size="sm" />
                          </div>
                        )}

                        <div className="flex items-center space-x-4 mb-4">
                          <div className="flex items-center">
                            <div className="flex mr-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${i < Math.round(displayRating) ? "text-yellow-400 fill-current" : "text-white/50"}`}
                                />
                              ))}
                            </div>
                            <span className="text-base font-semibold">{displayRating}</span>
                            <span className="text-sm text-blue-100 ml-1">({reviewCount} reviews)</span>
                          </div>
                        </div>

                        {tutor.sessionPreference && (
                          <div className="mb-3">
                            <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-sm">
                              {tutor.sessionPreference === "chat_only" && "💬 Chat & Messages Only"}
                              {tutor.sessionPreference === "video_only" && "📹 Video Sessions Only"}
                              {tutor.sessionPreference === "both" && "🎯 Chat & Video Sessions"}
                            </Badge>
                          </div>
                        )}

                        {tutor.subjects && tutor.subjects.length > 0 && (
                          <div className="space-y-2">
                            {(() => {
                              const { defaultOnes, approvedOnes } = categorizeSubjects(tutor.subjects);
                              return (
                                <>
                                  {defaultOnes.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {defaultOnes.slice(0, 4).map((subject, index) => (
                                        <Badge
                                          key={index}
                                          variant="secondary"
                                          className="bg-white/20 text-white border-0 text-xs"
                                        >
                                          {subject}
                                        </Badge>
                                      ))}
                                      {defaultOnes.length > 4 && (
                                        <Badge variant="secondary" className="bg-white/20 text-white border-0 text-xs">
                                          +{defaultOnes.length - 4} more
                                        </Badge>
                                      )}
                                    </div>
                                  )}

                                  {approvedOnes.length > 0 && (
                                    <div>
                                      <div className="flex items-center mb-1">
                                        <AlertTriangle className="h-3 w-3 mr-1 text-yellow-300" />
                                        <span className="text-xs text-blue-100">Specialized</span>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {approvedOnes.slice(0, 3).map((subject, index) => (
                                          <Badge
                                            key={index}
                                            variant="secondary"
                                            className="bg-yellow-400/20 text-yellow-100 border-yellow-300/30 text-xs"
                                          >
                                            {subject}
                                          </Badge>
                                        ))}
                                        {approvedOnes.length > 3 && (
                                          <Badge
                                            variant="secondary"
                                            className="bg-yellow-400/20 text-yellow-100 border-yellow-300/30 text-xs"
                                          >
                                            +{approvedOnes.length - 3} more
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Action buttons */}
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20 h-8 w-8 p-0"
                        onClick={handleToggleSave}
                      >
                        <Heart className={`h-5 w-5 ${isTutorSaved(tutor.id) ? "fill-current" : ""}`} />
                      </Button>

                      <Button
                        onClick={() => openBookingWithTracking("regular")}
                        className="bg-white text-blue-600 hover:bg-blue-50 h-9 text-sm px-4 whitespace-nowrap shadow-md font-medium"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Book Session - ابدأ الحجز
                      </Button>

                      <Button
                        onClick={() => setIsMessageModalOpen(true)}
                        className="bg-white/10 text-white hover:bg-white/20 border border-white/30 h-9 text-sm px-4 whitespace-nowrap backdrop-blur-sm font-medium"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Send Message
                      </Button>
                    </div>
                  </div>

                  {/* Stats Section - Redesigned with mobile optimization */}
                  <div className="mt-4 sm:mt-6">
                    {/* Mobile Layout: 2 columns */}
                    <div className="grid grid-cols-2 sm:hidden gap-1 items-stretch">
                      {/* Left: Pricing */}
                      <div className="bg-white/15 backdrop-blur-sm rounded-lg p-1 border border-white/20 flex flex-col">
                        <div className="flex flex-col items-center space-y-0.5 h-full justify-center">
                          <div className="text-sm font-semibold text-white/90">Prices</div>
                          {(() => {
                            const pricingOptions = [];
                            const isArabic = selectedCurrency.code === "EGP";

                            if (tutor.hourlyRate && tutor.hourlyRate > 0) {
                              const formattedPrice = formatTutorPrice(tutor.hourlyRate);
                              pricingOptions.push(
                                <PriceWithUsdBadge
                                  key="hourly"
                                  formattedPrice={formattedPrice}
                                  usdPrice={tutor.hourlyRate}
                                  size="lg"
                                  textColorClass="text-white"
                                  suffix={`/${getLocalizedText("hour")}`}
                                  suffixColorClass="text-blue-100/80"
                                />,
                              );
                            }

                            if (tutor.sessionRate && tutor.sessionRate > 0) {
                              const formattedPrice = formatTutorPrice(tutor.sessionRate);
                              pricingOptions.push(
                                <div key="session" className="text-center">
                                  <PriceWithUsdBadge
                                    formattedPrice={formattedPrice}
                                    usdPrice={tutor.sessionRate}
                                    size="md"
                                    textColorClass="text-green-200"
                                    suffix={`/${isArabic ? "جلسة" : getLocalizedText("per session")}`}
                                    suffixColorClass="text-blue-100/70"
                                  />
                                  <div className="text-blue-100/60 text-[10px] mt-0.5">(الجلسة أكثر من ساعة ونصف)</div>
                                </div>,
                              );
                            }

                            if (tutor.monthlyRate && tutor.monthlyRate > 0) {
                              const formattedPrice = formatTutorPrice(tutor.monthlyRate);
                              pricingOptions.push(
                                <PriceWithUsdBadge
                                  key="monthly"
                                  formattedPrice={formattedPrice}
                                  usdPrice={tutor.monthlyRate}
                                  size="md"
                                  textColorClass="text-purple-200"
                                  suffix={`/${isArabic ? "شهر" : getLocalizedText("per month")}`}
                                  suffixColorClass="text-blue-100/70"
                                />,
                              );
                            }

                            if (tutor.levelRates && Object.keys(tutor.levelRates).length > 0) {
                              const validLevelRates = Object.entries(tutor.levelRates).filter(
                                ([level, rate]) => Number(rate) > 0,
                              );
                              if (validLevelRates.length > 0) {
                                const sortedLevelRates = sortLevelRates(validLevelRates);
                                pricingOptions.push(
                                  <div key="levels" className="w-full text-center">
                                    <div className="text-[9px] font-medium text-orange-200 mb-0.5">
                                      {getLocalizedText("Pricing by Level")}
                                    </div>
                                    <div className="space-y-0.5">
                                      {sortedLevelRates.map(([level, rate]) => {
                                        const formattedPrice = formatTutorPrice(Number(rate));
                                        return (
                                          <div
                                            key={level}
                                            className="flex items-center justify-center gap-1 flex-wrap"
                                          >
                                            <span className="text-[8px] font-medium text-orange-100 capitalize break-words">
                                              {level}:
                                            </span>
                                            <PriceWithUsdBadge
                                              formattedPrice={formattedPrice}
                                              usdPrice={Number(rate)}
                                              size="sm"
                                              textColorClass="text-orange-200"
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>,
                                );
                              }
                            }

                            if (pricingOptions.length === 0) {
                              return (
                                <div className="flex items-center gap-1.5">
                                  <div className="text-xl font-bold text-white/60">N/A</div>
                                  <div className="text-sm text-blue-100/60">Contact</div>
                                </div>
                              );
                            }

                            return pricingOptions;
                          })()}
                        </div>
                      </div>

                      {/* Right: Rating, Reviews, Lessons stacked */}
                      <div className="flex flex-col gap-1 h-full justify-center">
                        <div className="bg-white/15 backdrop-blur-sm rounded-lg p-1 border border-white/20 flex items-center justify-center gap-2">
                          <Star className="h-5 w-5 text-yellow-300 fill-yellow-300 shrink-0" />
                          <div className="text-center">
                            <span className="text-lg font-bold text-white">{displayRating}</span>
                            <span className="text-xs text-blue-100/80 ml-1">Rating</span>
                          </div>
                        </div>

                        <div className="bg-white/15 backdrop-blur-sm rounded-lg p-1 border border-white/20 flex items-center justify-center gap-2">
                          <MessageSquare className="h-5 w-5 text-blue-200 shrink-0" />
                          <div className="text-center">
                            <span className="text-lg font-bold text-white">{reviewCount}</span>
                            <span className="text-xs text-blue-100/80 ml-1">Reviews</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tablet & Desktop Layout: 4 columns */}
                    <div className="hidden sm:grid grid-cols-4 gap-2 md:gap-3">
                      {/* Pricing Card */}
                      <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5 md:p-3 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                        <div className="flex flex-col items-center space-y-1.5 h-full">
                          <div className="p-1.5 bg-white/20 rounded-full shrink-0">
                            <CreditCard className="h-3.5 w-3.5 md:h-4 md:w-4 text-white" />
                          </div>
                          <div className="text-center w-full px-1">
                            {(() => {
                              const pricingOptions = [];
                              const isArabic = selectedCurrency.code === "EGP";

                              if (tutor.hourlyRate && tutor.hourlyRate > 0) {
                                const formattedPrice = formatTutorPrice(tutor.hourlyRate);
                                pricingOptions.push(
                                  <div key="hourly" className="mb-1">
                                    <PriceWithUsdBadge
                                      formattedPrice={formattedPrice}
                                      usdPrice={tutor.hourlyRate}
                                      size="md"
                                      textColorClass="text-white"
                                      suffix={`/${getLocalizedText("hour")}`}
                                      suffixColorClass="text-blue-100/80"
                                      className="justify-center"
                                    />
                                  </div>,
                                );
                              }

                              if (tutor.sessionRate && tutor.sessionRate > 0) {
                                const formattedPrice = formatTutorPrice(tutor.sessionRate);
                                pricingOptions.push(
                                  <div key="session" className="mb-1 text-center">
                                    <PriceWithUsdBadge
                                      formattedPrice={formattedPrice}
                                      usdPrice={tutor.sessionRate}
                                      size="sm"
                                      textColorClass="text-green-200"
                                      suffix={`/${isArabic ? "جلسة" : getLocalizedText("per session")}`}
                                      suffixColorClass="text-blue-100/70"
                                      className="justify-center"
                                    />
                                    <div className="text-blue-100/60 text-[8px] mt-0.5">(الجلسة أكثر من ساعة ونصف)</div>
                                  </div>,
                                );
                              }

                              if (tutor.monthlyRate && tutor.monthlyRate > 0) {
                                const formattedPrice = formatTutorPrice(tutor.monthlyRate);
                                pricingOptions.push(
                                  <div key="monthly" className="mb-1">
                                    <PriceWithUsdBadge
                                      formattedPrice={formattedPrice}
                                      usdPrice={tutor.monthlyRate}
                                      size="sm"
                                      textColorClass="text-purple-200"
                                      suffix={`/${isArabic ? "شهر" : getLocalizedText("per month")}`}
                                      suffixColorClass="text-blue-100/70"
                                      className="justify-center"
                                    />
                                  </div>,
                                );
                              }

                              if (tutor.levelRates && Object.keys(tutor.levelRates).length > 0) {
                                const validLevelRates = Object.entries(tutor.levelRates).filter(
                                  ([level, rate]) => Number(rate) > 0,
                                );
                                if (validLevelRates.length > 0) {
                                  const sortedLevelRates = sortLevelRates(validLevelRates);
                                  pricingOptions.push(
                                    <div key="levels" className="w-full text-center space-y-0.5 mt-1">
                                      <div className="text-[9px] md:text-[10px] font-medium text-orange-200">
                                        {getLocalizedText("Pricing by Level")}
                                      </div>
                                      <div className="space-y-0.5">
                                        {sortedLevelRates.map(([level, rate]) => {
                                          const formattedPrice = formatTutorPrice(Number(rate));
                                          return (
                                            <div
                                              key={level}
                                              className="flex items-center justify-center gap-1 flex-wrap"
                                            >
                                              <span className="text-[9px] font-medium text-orange-100 capitalize">
                                                {level}:
                                              </span>
                                              <PriceWithUsdBadge
                                                formattedPrice={formattedPrice}
                                                usdPrice={Number(rate)}
                                                size="sm"
                                                textColorClass="text-orange-200"
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>,
                                  );
                                }
                              }

                              if (pricingOptions.length === 0) {
                                return (
                                  <div className="space-y-0.5">
                                    <div className="text-base md:text-lg font-bold text-white/60">N/A</div>
                                    <div className="text-[10px] md:text-xs text-blue-100/60">Contact for pricing</div>
                                  </div>
                                );
                              }

                              return pricingOptions;
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Rating Card */}
                      <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5 md:p-3 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                        <div className="flex flex-col items-center space-y-1.5">
                          <div className="p-1.5 bg-yellow-400/20 rounded-full">
                            <Star className="h-3.5 w-3.5 md:h-4 md:w-4 text-yellow-300 fill-yellow-300" />
                          </div>
                          <div className="text-center">
                            <div className="text-xl md:text-2xl font-bold text-white">{displayRating}</div>
                            <div className="text-[10px] md:text-xs text-blue-100/80 mt-0.5">Rating</div>
                          </div>
                        </div>
                      </div>

                      {/* Reviews Card */}
                      <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5 md:p-3 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                        <div className="flex flex-col items-center space-y-1.5">
                          <div className="p-1.5 bg-blue-400/20 rounded-full">
                            <MessageSquare className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-200" />
                          </div>
                          <div className="text-center">
                            <div className="text-xl md:text-2xl font-bold text-white">{reviewCount}</div>
                            <div className="text-[10px] md:text-xs text-blue-100/80 mt-0.5">Reviews</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* First Session Info */}
            <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm text-blue-900 leading-relaxed">
                  In your first session, the tutor will assess your goals and level, create a personalized plan to get
                  you started, and answer any questions you have.
                </p>
                <p className="text-sm text-purple-900 leading-relaxed" dir="rtl">
                  في أول حصة، سيقوم المدرّس بتقييم أهدافك ومستواك، ويضع خطة شخصية لتبدأ بها، ويمكنك طرح أي أسئلة تريدها.
                </p>
              </CardContent>
            </Card>

            {/* Learning Plan Button */}
            <div className="mt-6">
              <Button
                onClick={() => setIsLearningPlanOpen(true)}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                <span className="mr-2">Learning Plan</span>
                <span className="mx-2">•</span>
                <span dir="rtl">خطة التعلم</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </div>

            {/* Mobile: Tabbed Interface */}
            <div className="lg:hidden">
              <TutorDetailsTabs
                tutor={tutor}
                localCoachInfo={localCoachInfo}
                localMusicCoachInfo={localMusicCoachInfo}
                localTravelGuideInfo={localTravelGuideInfo}
                formatTutorPrice={formatTutorPrice}
                getLocalizedText={getLocalizedText}
                formatAvailability={formatAvailability}
                getYouTubeEmbedUrl={getYouTubeEmbedUrl}
                responseTime={responseTime}
                lessonsCount={lessonsCount}
                lessons={lessons}
                categorizeSubjects={categorizeSubjects}
                onBookSession={(type) => openBookingWithTracking(type)}
                onSendMessage={() => setIsMessageModalOpen(true)}
              />
            </div>

            {/* Desktop: Regular Layout */}
            <div className="hidden lg:block space-y-6">
              {/* About Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
                    About {tutorName.split(" ")[0]}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none">
                    <div className="leading-relaxed text-base break-words whitespace-pre-wrap">
                      {(
                        tutor.bio ||
                        "This tutor has not provided a detailed bio yet. However, they are experienced in their field and ready to help you achieve your learning goals."
                      )
                        .split("\n")
                        .map((paragraph, index) => {
                          const direction = detectTextDirection(paragraph);
                          const textAlign = direction === "rtl" ? "right" : "left";
                          const textColor = direction === "rtl" ? "text-blue-700" : "text-gray-700";

                          return (
                            <p
                              key={index}
                              className={`mb-2 last:mb-0 ${textColor}`}
                              style={{
                                direction,
                                textAlign,
                                unicodeBidi: "plaintext",
                              }}
                            >
                              {wrapTextWithBidiMarkers(paragraph)}
                            </p>
                          );
                        })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Methods */}
              {tutor.paymentMethods && tutor.paymentMethods.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-xl">
                      <CreditCard className="h-6 w-6 mr-3 text-green-600" />
                      Payment Methods
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {tutor.paymentMethods.map((methodId) => {
                        const getPaymentMethodIcon = (methodId: string) => {
                          switch (methodId.toLowerCase()) {
                            case "fawry":
                              return CreditCard;
                            case "instapay":
                              return Smartphone;
                            default:
                              return CreditCard;
                          }
                        };

                        const getPaymentMethodName = (methodId: string) => {
                          switch (methodId.toLowerCase()) {
                            case "fawry":
                              return "Fawry";
                            case "instapay":
                              return "InstaPay";
                            default:
                              return methodId;
                          }
                        };

                        const PaymentIcon = getPaymentMethodIcon(methodId);
                        return (
                          <Badge
                            key={methodId}
                            variant="secondary"
                            className="bg-green-100 text-green-700 border border-green-200 text-lg py-2 px-4"
                          >
                            <PaymentIcon className="h-5 w-5 mr-2" />
                            {getPaymentMethodName(methodId)}
                          </Badge>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Experience & Education */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                    Experience & Education
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <Award className="h-4 w-4 mr-2 text-orange-500" />
                      Teaching Experience
                    </h4>
                    <p className="text-gray-600 break-words">
                      {tutor.experience || "Professional teaching experience"}
                    </p>
                  </div>

                  {tutor.education && Array.isArray(tutor.education) && tutor.education.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <GraduationCap className="h-4 w-4 mr-2 text-blue-500" />
                        Education
                      </h4>
                      <div className="space-y-3">
                        {tutor.education.map((edu: any, index: number) => (
                          <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                            <div className="font-medium text-gray-900 break-words">{edu.degree}</div>
                            <div className="text-gray-600 break-words">{edu.institution}</div>
                            <div className="text-sm text-gray-500">Graduated: {edu.year}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <Globe className="h-4 w-4 mr-2 text-green-500" />
                      Languages
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tutor.languages && tutor.languages.length > 0 ? (
                        tutor.languages.map((language, index) => (
                          <Badge key={index} variant="outline" className="text-sm">
                            {language}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-gray-500">Languages not specified</span>
                      )}
                    </div>
                  </div>

                  {tutor.grades && tutor.grades.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <GraduationCap className="h-4 w-4 mr-2 text-purple-500" />
                        Grade Levels Taught
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {tutor.grades.map((grade, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="text-sm bg-purple-50 text-purple-700 border-purple-200"
                          >
                            {grade}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Local Coaching Information */}
              {localCoachInfo && (
                <Card className="border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg text-green-800">
                      <MapPin className="h-5 w-5 mr-2" />
                      Local Fitness Coaching Available
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">In-Person Training</h4>
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified Local Coach
                        </Badge>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Location:</strong> {localCoachInfo.location?.city}, {localCoachInfo.location?.state}
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Service Radius:</strong> {localCoachInfo.coverageRadius || 10} miles
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Local Rate:</strong>{" "}
                            <span className="text-green-600 font-semibold">
                              {formatTutorPrice(localCoachInfo.hourlyRate)}/{getLocalizedText("hour")}
                            </span>
                          </p>
                        </div>

                        <div>
                          {localCoachInfo.specialties && localCoachInfo.specialties.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-900 mb-1">Specialties:</p>
                              <div className="flex flex-wrap gap-1">
                                {localCoachInfo.specialties.slice(0, 3).map((specialty: string, index: number) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {specialty}
                                  </Badge>
                                ))}
                                {localCoachInfo.specialties.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{localCoachInfo.specialties.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-200">
                        {localCoachInfo.acceptsHomeVisits && (
                          <div className="flex items-center text-sm text-green-700">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Home visits available
                          </div>
                        )}
                        {localCoachInfo.hasOwnEquipment && (
                          <div className="flex items-center text-sm text-green-700">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Brings own equipment
                          </div>
                        )}
                        <div className="flex items-center text-sm text-green-700">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {localCoachInfo.experience || 0} years local experience
                        </div>
                      </div>

                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 mt-4"
                        onClick={() => openBookingWithTracking("coaching")}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Book Local Fitness Coaching Session
                      </Button>
                    </div>

                    <p className="text-sm text-gray-600 italic">
                      💡 This tutor also offers in-person fitness coaching in their local area. Perfect for hands-on
                      training and personalized fitness guidance!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Local Music Coaching Information */}
              {localMusicCoachInfo && (
                <Card className="border-purple-200 bg-purple-50">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg text-purple-800">
                      <MapPin className="h-5 w-5 mr-2" />
                      Local Music Coaching Available
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">In-Person Music Lessons</h4>
                        <Badge className="bg-purple-100 text-purple-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified Local Music Coach
                        </Badge>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Location:</strong> {localMusicCoachInfo.location?.city},{" "}
                            {localMusicCoachInfo.location?.state}
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Service Radius:</strong> {localMusicCoachInfo.coverageRadius || 10} miles
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Local Rate:</strong>{" "}
                            <span className="text-purple-600 font-semibold">
                              {formatTutorPrice(localMusicCoachInfo.hourlyRate)}/{getLocalizedText("hour")}
                            </span>
                          </p>
                        </div>

                        <div>
                          {localMusicCoachInfo.specialties && localMusicCoachInfo.specialties.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-900 mb-1">Specialties:</p>
                              <div className="flex flex-wrap gap-1">
                                {localMusicCoachInfo.specialties.slice(0, 3).map((specialty: string, index: number) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {specialty}
                                  </Badge>
                                ))}
                                {localMusicCoachInfo.specialties.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{localMusicCoachInfo.specialties.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-200">
                        {localMusicCoachInfo.acceptsHomeVisits && (
                          <div className="flex items-center text-sm text-purple-700">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Home visits available
                          </div>
                        )}
                        {localMusicCoachInfo.hasOwnInstruments && (
                          <div className="flex items-center text-sm text-purple-700">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Brings own instruments
                          </div>
                        )}
                        <div className="flex items-center text-sm text-purple-700">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {localMusicCoachInfo.experience || 0} years local experience
                        </div>
                      </div>

                      <Button
                        className="w-full bg-purple-600 hover:bg-purple-700 mt-4"
                        onClick={() => openBookingWithTracking("music_coaching")}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Book Local Music Coaching Session
                      </Button>
                    </div>

                    <p className="text-sm text-gray-600 italic">
                      🎵 This tutor also offers in-person music coaching in their local area. Perfect for hands-on
                      instruction and personalized music guidance!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Local Travel Guide Information */}
              {localTravelGuideInfo && (
                <Card className="border-blue-200 bg-blue-50">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg text-blue-800">
                      <MapPin className="h-5 w-5 mr-2" />
                      Local Travel Guide Available
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">In-Person Travel Guidance</h4>
                        <Badge className="bg-blue-100 text-blue-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified Local Travel Guide
                        </Badge>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Location:</strong> {localTravelGuideInfo.location?.city},{" "}
                            {localTravelGuideInfo.location?.state}
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Service Radius:</strong> {localTravelGuideInfo.coverageRadius || 10} miles
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Local Rate:</strong>{" "}
                            <span className="text-green-600 font-semibold">
                              {formatTutorPrice(localTravelGuideInfo.hourlyRate)}/{getLocalizedText("hour")}
                            </span>
                          </p>
                        </div>

                        <div>
                          {localTravelGuideInfo.specialties && localTravelGuideInfo.specialties.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-900 mb-1">Specialties:</p>
                              <div className="flex flex-wrap gap-1">
                                {localTravelGuideInfo.specialties
                                  .slice(0, 3)
                                  .map((specialty: string, index: number) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      {specialty}
                                    </Badge>
                                  ))}
                                {localTravelGuideInfo.specialties.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{localTravelGuideInfo.specialties.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-200">
                        {localTravelGuideInfo.acceptsHomeVisits && (
                          <div className="flex items-center text-sm text-green-700">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Meetup available
                          </div>
                        )}
                        {localTravelGuideInfo.hasOwnTransport && (
                          <div className="flex items-center text-sm text-green-700">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Has own transport
                          </div>
                        )}
                        <div className="flex items-center text-sm text-green-700">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {localTravelGuideInfo.experience || 0} years local experience
                        </div>
                      </div>

                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 mt-4"
                        onClick={() => openBookingWithTracking("travel_coaching")}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Book Local Travel Coaching Session
                      </Button>
                    </div>

                    <p className="text-sm text-gray-600 italic">
                      🗺️ This tutor also offers in-person travel guidance in their local area. Perfect for local
                      exploration and personalized travel tips!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Enhanced Subjects Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
                    Subjects & Expertise
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {tutor.subjects && tutor.subjects.length > 0 ? (
                    (() => {
                      const { defaultOnes, approvedOnes } = categorizeSubjects(tutor.subjects);
                      return (
                        <div className="space-y-6">
                          {/* Default subjects */}
                          {defaultOnes.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2 text-blue-500" />
                                Core Subjects
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {defaultOnes.map((subject, index) => (
                                  <Badge key={index} variant="secondary" className="text-sm">
                                    {subject}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Approved/specialized subjects */}
                          {approvedOnes.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <AlertTriangle className="h-4 w-4 mr-2 text-green-600" />
                                Specialized Subjects
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {approvedOnes.map((subject, index) => (
                                  <Badge
                                    key={index}
                                    variant="default"
                                    className="bg-green-100 text-green-800 border-green-300 text-sm"
                                  >
                                    {subject}
                                  </Badge>
                                ))}
                              </div>
                              <p className="text-sm text-gray-600 mt-2">
                                These specialized subjects have been approved by our admin team based on the tutor's
                                expertise.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="text-gray-500">No subjects specified</p>
                  )}
                </CardContent>
              </Card>

              {/* Availability - Full Calendar View */}
              <TutorAvailabilityCalendar tutor={tutor} />

              {/* Reviews Section with proper canAddReview logic */}
              <TutorReviews tutorId={tutor.id} canAddReview={user?.role === "student"} />
            </div>
          </div>

          {/* Right Column - Booking Section - Hidden on mobile, shown on desktop */}
          <div className="hidden lg:block lg:col-span-1 min-w-0">
            <div className="sticky top-8 space-y-6">
              {/* Booking Card */}
              <Card className="shadow-lg">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="space-y-3">
                      {/* Show Primary Pricing Type */}
                      {tutor.pricingType && (
                        <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-2">
                          {tutor.pricingType === "hourly" && getLocalizedText("Per Hour")}
                          {tutor.pricingType === "session" && getLocalizedText("Per Session")}
                          {tutor.pricingType === "level" && getLocalizedText("By Level")}
                          {tutor.pricingType === "monthly" && getLocalizedText("Monthly Subscription")}
                        </div>
                      )}

                      {/* Show ALL pricing options that have values (same logic as Book tab) */}
                      {(() => {
                        const pricingOptions = [] as JSX.Element[];
                        const isArabic = selectedCurrency.code === "EGP";

                        if (tutor.hourlyRate && tutor.hourlyRate > 0) {
                          const formattedPrice = formatTutorPrice(tutor.hourlyRate);
                          if (formattedPrice) {
                            pricingOptions.push(
                              isArabic ? (
                                <div key="hourly" className="mb-2">
                                  <div className="flex items-baseline gap-1 justify-center" dir="rtl">
                                    <span className="text-lg md:text-xl font-bold text-gray-900">
                                      {formattedPrice.replace(selectedCurrency.symbol, "").trim()}
                                    </span>
                                    <span className="text-base md:text-lg font-semibold text-gray-900">
                                      {selectedCurrency.symbol}
                                    </span>
                                  </div>
                                  <div className="text-gray-500 text-[10px] md:text-xs">
                                    / {getLocalizedText("hour")}
                                  </div>
                                </div>
                              ) : (
                                <div key="hourly" className="mb-2">
                                  <div className="text-lg md:text-xl font-bold text-gray-900">{formattedPrice}</div>
                                  <div className="text-gray-500 text-[10px] md:text-xs">
                                    {getLocalizedText("per hour")}
                                  </div>
                                </div>
                              ),
                            );
                          }
                        }

                        if (tutor.sessionRate && tutor.sessionRate > 0) {
                          const formattedPrice = formatTutorPrice(tutor.sessionRate);
                          if (formattedPrice) {
                            pricingOptions.push(
                              isArabic ? (
                                <div key="session" className="mb-2">
                                  <div className="flex items-baseline gap-1 justify-center" dir="rtl">
                                    <span className="text-lg md:text-xl font-bold text-green-600">
                                      {formattedPrice.replace(selectedCurrency.symbol, "").trim()}
                                    </span>
                                    <span className="text-base md:text-lg font-semibold text-green-600">
                                      {selectedCurrency.symbol}
                                    </span>
                                  </div>
                                  <div className="text-gray-500 text-[10px] md:text-xs">/ جلسة</div>
                                  <div className="text-gray-400 text-[8px] md:text-[10px] mt-0.5">(الجلسة أكثر من ساعة ونصف)</div>
                                </div>
                              ) : (
                                <div key="session" className="mb-2">
                                  <div className="text-lg md:text-xl font-bold text-green-600">{formattedPrice}</div>
                                  <div className="text-gray-500 text-[10px] md:text-xs">
                                    {getLocalizedText("per session")}
                                  </div>
                                  <div className="text-gray-400 text-[8px] md:text-[10px] mt-0.5">(الجلسة أكثر من ساعة ونصف)</div>
                                </div>
                              ),
                            );
                          }
                        }

                        if (tutor.monthlyRate && tutor.monthlyRate > 0) {
                          const formattedPrice = formatTutorPrice(tutor.monthlyRate);
                          if (formattedPrice) {
                            pricingOptions.push(
                              isArabic ? (
                                <div key="monthly" className="mb-2">
                                  <div className="flex items-baseline gap-1 justify-center" dir="rtl">
                                    <span className="text-lg md:text-xl font-bold text-purple-600">
                                      {formattedPrice.replace(selectedCurrency.symbol, "").trim()}
                                    </span>
                                    <span className="text-base md:text-lg font-semibold text-purple-600">
                                      {selectedCurrency.symbol}
                                    </span>
                                  </div>
                                  <div className="text-gray-500 text-[10px] md:text-xs">/ شهر</div>
                                </div>
                              ) : (
                                <div key="monthly" className="mb-2">
                                  <div className="text-lg md:text-xl font-bold text-purple-600">{formattedPrice}</div>
                                  <div className="text-gray-500 text-[10px] md:text-xs">
                                    {getLocalizedText("per month")}
                                  </div>
                                </div>
                              ),
                            );
                          }
                        }

                        if (tutor.levelRates && Object.keys(tutor.levelRates).length > 0) {
                          const validLevelRates = Object.entries(tutor.levelRates).filter(
                            ([_, rate]) => Number(rate) > 0,
                          );
                          if (validLevelRates.length > 0) {
                            const sortedLevelRates = sortLevelRates(validLevelRates);
                            pricingOptions.push(
                              <div key="level" className="mb-2 bg-orange-50 rounded-lg p-3 border border-orange-200">
                                <div className="text-[10px] md:text-xs font-medium text-orange-800 mb-1.5 text-center">
                                  {getLocalizedText("Pricing by Level")}
                                </div>
                                <div className="space-y-1">
                                  {sortedLevelRates.map(([level, rate]) => {
                                    const formattedPrice = formatTutorPrice(Number(rate));
                                    const isArabic = selectedCurrency.code === "EGP";
                                    return (
                                      <div key={level} className="flex items-baseline justify-center gap-1 flex-wrap">
                                        <span className="capitalize font-medium text-gray-700 text-[10px] md:text-[10px]">
                                          {level}:
                                        </span>
                                        {isArabic ? (
                                          <>
                                            <span className="font-bold text-orange-600 text-[10px] md:text-[10px]">
                                              {selectedCurrency.symbol}
                                            </span>
                                            <span className="font-bold text-orange-600 text-[10px] md:text-[10px]">
                                              {formattedPrice.replace(selectedCurrency.symbol, "").trim()}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="font-bold text-orange-600 text-[10px] md:text-[10px]">
                                            {formattedPrice}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>,
                            );
                          }
                        }

                        if (pricingOptions.length === 0) {
                          return (
                            <div className="mb-2 text-center py-2">
                              <div className="text-base font-bold text-gray-400">Contact for pricing</div>
                            </div>
                          );
                        }

                        return pricingOptions;
                      })()}
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <Button
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => openBookingWithTracking("regular")}
                    >
                      <Calendar className="h-5 w-5 mr-2" />
                      Book Session - ابدأ الحجز
                    </Button>

                    <Button variant="outline" size="lg" className="w-full" onClick={() => setIsMessageModalOpen(true)}>
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Send Message
                    </Button>
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Response time</span>
                      <span className="font-medium">{responseTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Lessons taught</span>
                      <span className="font-medium">{lessonsCount}+</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Introduction Video */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <Video className="h-5 w-5 mr-2 text-blue-600" />
                    Introduction Video
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const hasUploadedVideo = tutor.introVideoFile && tutor.introVideoFile.trim() !== "";
                    const hasVideoUrl = tutor.introVideo && tutor.introVideo.trim() !== "";

                    if (hasUploadedVideo) {
                      // Display uploaded video file
                      const videoUrl = pb.files.getURL(tutor, tutor.introVideoFile);
                      return (
                        <div className="aspect-video rounded-lg overflow-hidden bg-gray-900">
                          <video
                            src={videoUrl}
                            className="w-full h-full object-contain"
                            controls
                            preload="metadata"
                            playsInline
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      );
                    } else if (hasVideoUrl) {
                      const embedUrl = convertToEmbeddableUrl(tutor.introVideo);
                      return (
                        <div className="aspect-video rounded-lg overflow-hidden relative">
                          <iframe
                            src={`${embedUrl}?modestbranding=1&rel=0&showinfo=0`}
                            title="Tutor Introduction Video"
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          />
                          {/* Top overlay to block channel name clicks */}
                          <div
                            className="absolute top-0 left-0 right-0 h-14 cursor-default"
                            onClick={(e) => e.preventDefault()}
                          />
                          {/* Bottom-right overlay to block YouTube logo */}
                          <div
                            className="absolute bottom-0 right-0 w-24 h-10 cursor-default"
                            onClick={(e) => e.preventDefault()}
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div className="bg-gray-100 rounded-lg p-6 text-center">
                          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Play className="h-8 w-8 text-blue-600" />
                          </div>
                          <p className="text-gray-600 mb-4">Introduction video coming soon</p>
                          <Button variant="outline" size="sm" disabled>
                            <Play className="h-4 w-4 mr-2" />
                            Watch Video
                          </Button>
                        </div>
                      );
                    }
                  })()}
                </CardContent>
              </Card>

              {/* Quick Facts */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Facts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">Verified Tutor</div>
                      <div className="text-sm text-gray-500">Profile verified by our team</div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Zap className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">Quick Responses</div>
                      <div className="text-sm text-gray-500">Usually replies within 2 hours</div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Award className="h-5 w-5 text-purple-500 mr-3 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">Top Rated</div>
                      <div className="text-sm text-gray-500">Highly rated by students</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Inspiration Posts Feed */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Shared Inspirations</CardTitle>
                  <CardDescription>Posts shared by {tutorName.split(" ")[0]}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[500px] overflow-y-auto px-6 pb-6 scrollbar-thin">
                    <TutorPostsTab tutorId={tutorId || ""} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Related Lessons Section - Hidden on mobile, shown on desktop */}
      <div className="hidden lg:block bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Lessons from {tutorName}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore lessons created by this tutor and enhance your learning experience
            </p>
          </div>

          {lessons && lessons.length > 0 ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {lessons.slice(0, 3).map((lesson) => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    tutorData={{
                      profile: tutor,
                      user: tutor?.expand?.user,
                    }}
                    canEditLesson={() => false}
                    handleViewLesson={(lesson) => navigate(`/lesson/${lesson.id}`)}
                    handleEditLesson={() => {}}
                    handleDeleteLesson={() => {}}
                    handleJoinLesson={(lesson) => navigate(`/messages?tutor=${tutor?.id}&lesson=${lesson.id}`)}
                    handleBookSession={() => openBookingWithTracking("regular")}
                    formatDuration={(minutes) => {
                      const hours = Math.floor(minutes / 60);
                      const mins = minutes % 60;
                      if (hours > 0) {
                        return `${hours}h ${mins}m`;
                      }
                      return `${mins} min`;
                    }}
                    hasVideo={(lesson) => !!(lesson.videoFile || lesson.videoUrl)}
                    hasAttachment={(lesson) => !!lesson.attachment}
                    hasContent={(lesson) => !!lesson.content}
                  />
                ))}
              </div>

              {lessons.length > 3 && (
                <div className="text-center">
                  <Button
                    onClick={() => navigate(`/tutor-lessons?tutorId=${tutorId}`)}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                  >
                    <BookOpen className="h-5 w-5 mr-2" />
                    View All {lessons.length} Lessons
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No lessons available yet</h3>
              <p className="text-gray-500">This tutor hasn't created any lessons yet. Check back later!</p>
            </div>
          )}
        </div>
      </div>

      <Footer />

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        tutor={tutor}
        customPrice={
          bookingType === "coaching"
            ? localCoachInfo?.hourlyRate
            : bookingType === "music_coaching"
              ? localMusicCoachInfo?.hourlyRate
              : bookingType === "travel_coaching"
                ? localTravelGuideInfo?.hourlyRate
                : undefined
        }
        isLocalCoach={
          bookingType === "coaching" || bookingType === "music_coaching" || bookingType === "travel_coaching"
        }
        localCoachData={
          bookingType === "coaching"
            ? localCoachInfo
            : bookingType === "music_coaching"
              ? localMusicCoachInfo
              : bookingType === "travel_coaching"
                ? localTravelGuideInfo
                : undefined
        }
        initialSubject={
          bookingType === "music_coaching" ? "Music" : bookingType === "travel_coaching" ? "Travel" : undefined
        }
      />

      <SendMessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} tutor={tutor} />

      <ImageModal
        isOpen={isPhotoModalOpen}
        onClose={() => setIsPhotoModalOpen(false)}
        imageUrl={profilePictureUrl}
        title={tutorName}
      />

      {/* Learning Plan Modal */}
      {tutorId && (
        <LearningPlanModal
          isOpen={isLearningPlanOpen}
          onClose={() => setIsLearningPlanOpen(false)}
          tutorId={tutorId}
          isOwnProfile={user?.id === tutor?.user}
          tutorAbout={tutor?.bio}
        />
      )}
    </div>
  );
};

export default TutorDetails;
